/**
 * Accept Invitation Component
 * Allows users to accept an invitation and create their account
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  Link,
} from '@mui/material';
import { PersonAddOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api';

const AcceptInvitation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('auth.invitation.no_token', 'No invitation token provided'));
    }
  }, [token, t]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    // Validation
    if (!formData.username.trim()) {
      setError(t('auth.invitation.username_required', 'Username is required'));
      return;
    }

    if (!formData.password) {
      setError(t('auth.invitation.password_required', 'Password is required'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.invitation.password_too_short', 'Password must be at least 6 characters'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.invitation.password_mismatch', 'Passwords do not match'));
      return;
    }

    if (!token) {
      setError(t('auth.invitation.no_token', 'No invitation token provided'));
      return;
    }

    setLoading(true);
    try {
      await apiService.post('/api/auth/accept-invitation', {
        token,
        username: formData.username.trim(),
        password: formData.password,
        full_name: formData.full_name.trim() || undefined,
      });

      setSuccess(true);
      
      // Auto-login after successful registration
      setTimeout(async () => {
        try {
          await apiService.login({
            username: formData.username.trim(),
            password: formData.password,
          });
          navigate('/dashboard', { replace: true });
        } catch (loginError: any) {
          // If auto-login fails, redirect to login page
          navigate('/login', { 
            replace: true,
            state: { message: t('auth.invitation.account_created', 'Account created successfully. Please log in.') }
          });
        }
      }, 2000);
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        responseData: err.response?.data,
        status: err.response?.status,
        detail: err.detail,
        fullError: JSON.stringify(err, Object.getOwnPropertyNames(err), 2),
      });
      
      // Extract error message - handle both APIError format and Axios error format
      let errorMessage = t('auth.invitation.accept_error', 'Failed to accept invitation. Please check your token and try again.');
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.detail) {
        errorMessage = err.detail;
      } else if (err.message && err.message !== 'Network Error') {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ m: 1, bgcolor: 'error.main' }}>
                  <PersonAddOutlined />
                </Avatar>
                <Typography component="h1" variant="h5">
                  {t('auth.invitation.invalid', 'Invalid Invitation')}
                </Typography>
              </Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                {t('auth.invitation.no_token', 'No invitation token provided')}
              </Alert>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{ mt: 2 }}
              >
                {t('auth.signIn', 'Sign In')}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ m: 1, bgcolor: 'success.main' }}>
                  <PersonAddOutlined />
                </Avatar>
                <Typography component="h1" variant="h5">
                  {t('auth.invitation.success', 'Account Created!')}
                </Typography>
              </Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('auth.invitation.success_message', 'Your account has been created successfully. Redirecting...')}
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                <PersonAddOutlined />
              </Avatar>
              <Typography component="h1" variant="h5">
                {t('auth.invitation.title', 'Accept Invitation')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                {t('auth.invitation.subtitle', 'Create your account to get started')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label={t('auth.username', 'Username')}
                name="username"
                autoComplete="username"
                autoFocus
                value={formData.username}
                onChange={handleInputChange}
                disabled={loading}
              />
              <TextField
                margin="normal"
                fullWidth
                id="full_name"
                label={t('auth.name', 'Full Name')}
                name="full_name"
                autoComplete="name"
                value={formData.full_name}
                onChange={handleInputChange}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('auth.password', 'Password')}
                type="password"
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleInputChange}
                disabled={loading}
                helperText={t('auth.invitation.password_hint', 'Minimum 6 characters')}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('auth.invitation.confirm_password', 'Confirm Password')}
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('auth.invitation.create_account', 'Create Account')
                )}
              </Button>
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ cursor: 'pointer' }}
                >
                  {t('auth.switchToSignIn', 'Already have an account? Sign in')}
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default AcceptInvitation;
